import React, { useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { jobErrorText } from "@/lib/jobErrorText";   // Step 11d · A2: a failed job's error in the screen's language

const REQUIRED = [
  "Transaction Type", "Transaction ID", "Transaction Date", "Amount",
];
const OPTIONAL = [
  "Customer Name", "Customer ID", "Due Date", "Currency",
  "Payment Terms Days", "Reference Invoice ID",
];

const POLL_MS = 2000;

export default function SubLedgerUploadDialog({ engagementId, onClose, onIngested }) {
  const { t, lang } = useI18n();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const submit = async () => {
    if (!file) { toast.error(t("digitalAudit.pickFile")); return; }
    setBusy(true); setMsg(t("digitalAudit.uploading"));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(
        `/digital-audit/${engagementId}/sub-ledger/upload`,
        fd, { headers: { "Content-Type": "multipart/form-data" }});
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("No job_id in response");
      // Poll job to completion.
      let elapsed = 0;
      const MAX = 15 * 60 * 1000;
      while (elapsed < MAX) {
        await new Promise((res) => setTimeout(res, POLL_MS));
        elapsed += POLL_MS;
        const jr = await api.get(`/jobs/${jobId}`);
        setMsg(jr.data?.message || "Working…");
        if (jr.data?.status === "done") {
          const res = jr.data?.result || {};
          toast.success(
            `Sub-ledger ingested: ${res.rows_ingested?.toLocaleString?.() ?? 0} rows`
            + (res.dropped_total ? ` (dropped ${res.dropped_total})` : ""));
          onIngested?.(res);
          onClose?.();
          return;
        }
        if (jr.data?.status === "failed") {
          throw new Error(jobErrorText(jr.data, lang, t, "Upload job failed"));
        }
      }
      throw new Error("Upload job timed out");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally {
      setBusy(false); setMsg("");
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="sub-ledger-upload-dialog">
        <DialogHeader>
          <DialogTitle>{t("digitalAudit.subLedger.title")}</DialogTitle>
          <DialogDescription>
            {t("digitalAudit.subLedger.desc")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">
            <div className="mb-1 font-medium text-foreground">{t("digitalAudit.requiredCols")}</div>
            <ul className="list-disc ps-5 space-y-0.5">
              {REQUIRED.map((c) => <li key={c}>{c}</li>)}
            </ul>
            <div className="mt-2 mb-1 font-medium text-foreground">{t("digitalAudit.optionalCols")}</div>
            <ul className="list-disc ps-5 space-y-0.5">
              {OPTIONAL.map((c) => <li key={c}>{c}</li>)}
            </ul>
            <div className="mt-2 text-[11px]">
              {t("b3.txTypeAccepts")} <code>invoice</code>, <code>credit_note</code>,
              {" "}<code>payment</code>, <code>write_off</code> (aliases like
              {" "}<code>CN</code>, <code>receipt</code>, <code>writeoff</code> also accepted).
            </div>
          </div>

          <input
            type="file"
            accept=".csv,.xls,.xlsx,.xlsm,.xlsb"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            disabled={busy}
            className="block w-full text-sm"
            data-testid="sub-ledger-upload-file"
          />

          {msg && (
            <div className="rounded-md border bg-muted/40 p-2 text-xs">
              {busy && <Loader2 className="h-3 w-3 inline me-1 animate-spin" />}
              {msg}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={busy}
                     data-testid="sub-ledger-upload-cancel">{t("common.cancel")}</Button>
            <Button onClick={submit} disabled={busy || !file}
                     data-testid="sub-ledger-upload-submit">
              {busy ? <Loader2 className="h-3 w-3 me-1 animate-spin" /> : <Upload className="h-3 w-3 me-1" />}
              {busy ? t("digitalAudit.uploading") : t("digitalAudit.upload")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
